// ORP-18644 AC3, phase A — the report read registry v4 never had.
//
// SCOPE: in-flight sharing ONLY. Two sections asking the identical question at the same time cost
// one controller call instead of two 120s calls; the entry is dropped the instant the read settles,
// so nothing is ever served from a finished response.
//
// That boundary is AC3's own, not a shortcut. The AC licenses reuse "only when the cached levels'
// `primary_fields` + `tableDimensions` cover the requested dims" — reuse is gated on a coverage
// check, and v4 cannot perform it yet: per-level `tableDimensions` comes from `getPartitionColumns`,
// which is ORP-18643. No gate, no licensed reuse. Phase B adds the gate and lets settled answers
// live on; until then serving one would risk answering a request with another request's data, and
// would silently defeat `reload()` and the server-side cache-clear button.
//
// v3 kept its cache at `utils/reportCache.js` and it was the source of the hazard AC6's abort→clear
// ordering exists to prevent: it handed out promises the navigation abort had already rejected. The
// contract is ported; the mechanism is not (standing rule — v4 fixes what v3 got wrong):
//
//   * an entry is retracted as soon as its read settles, in success or failure, so a rejected or
//     cancelled promise can never be joined by the next caller. Retraction alone is not enough:
//     an abort rejects ASYNCHRONOUSLY, and a tab switch unmounts the owner and mounts its
//     replacement in one flush, so the joiner can still reach a doomed entry first. A joiner that
//     observes the owner's cancellation therefore drops the entry and opens its own read;
//   * the retraction is identity-checked, so a late rejection cannot evict the *replacement* entry
//     that a nav clear plus refetch already put under the same key.
//
// One more v3 rule deliberately NOT ported: `shouldUseCache = !signal` (`reportStore.ts:123`), which
// skipped the cache whenever a signal was present. Every v4 read carries a per-component signal from
// `useReportData`, so porting it literally would disable sharing outright. Export is the real bypass.

import type { ReportResponse } from '../models/report.types'

import type { ResolvedReportRequest } from './reportRequest'

import { ApiError } from '@/shared/http'

const inFlight = new Map<string, Promise<ReportResponse>>()

function isCanceled(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.kind === 'canceled'
}

/**
 * The sharing key, or `null` when this read must not be shared at all.
 *
 * Only the single-workspace schedule controller qualifies — v3 computed a key in that branch alone
 * (AC2 marks it "cacheable" and the other three shapes not). Exports bypass: their answer is a job
 * envelope rather than report rows, and a second exporter must get its own job.
 */
export function reportRequestCacheKey(
  request: ResolvedReportRequest,
  exportRequest?: boolean,
): string | null {
  if (exportRequest === true || request.kind !== 'schedule') return null
  return `${request.url}${request.query}`
}

/**
 * Share one in-flight read under `key`: a caller arriving while it is still open joins that promise
 * instead of opening a second request. Once it settles the entry is gone, so the next caller always
 * gets a fresh read.
 */
export function shareInFlightReportRequest(
  key: string,
  start: () => Promise<ReportResponse>,
): Promise<ReportResponse> {
  const existing = inFlight.get(key)
  if (existing) {
    /**
     * A joiner must never inherit the OWNER's cancellation. The entry is retracted when the read
     * settles, but an abort rejects asynchronously: on a tab switch or an SPA navigation Vue
     * unmounts the old surface and mounts the new one in the SAME flush, so the replacement reads
     * this map while the owner's rejection is still queued and joins a promise that is already
     * doomed. It then observes a cancel it never asked for. Retract the doomed entry ourselves and
     * open the read we actually requested — the share is an optimisation, never a shared fate.
     */
    return existing.catch((cause: unknown) => {
      if (!isCanceled(cause)) throw cause
      if (inFlight.get(key) === existing) inFlight.delete(key)
      return shareInFlightReportRequest(key, start)
    })
  }

  const pending = start()
  inFlight.set(key, pending)
  const retract = (): void => {
    // Identity-checked: a settling read must only ever remove ITS OWN entry. Without this, a promise
    // that rejects after a nav clear would evict the fresh entry a new page had already registered,
    // and the caller after that would open a duplicate request.
    if (inFlight.get(key) === pending) inFlight.delete(key)
  }
  pending.then(retract, retract)
  return pending
}

/**
 * Per-navigation drop, wired through 05's `registerNavReset` — AC6's "clears the cache" with its
 * real subject. Entries still open here belong to a page the user has left; dropping them means the
 * next page can never join a promise the navigation abort is about to reject.
 */
export function clearReportRequestCache(): void {
  inFlight.clear()
}

/** Test seam: open-entry count, so specs assert the registry does not leak instead of guessing. */
export function reportRequestCacheSize(): number {
  return inFlight.size
}
